import React, { createContext, useContext, useReducer } from 'react'

const DrawingContext = createContext()

// 초기 상태 정의
const initialState = {
  // 도구 설정
  tool: 'pen',
  color: '#000000',
  brushSize: 3,
  opacity: 100,
  
  // 레이어
  layers: [
    { id: 'layer-1', name: '레이어 1', visible: true, active: true },
    { id: 'layer-2', name: '레이어 2', visible: false, active: false }
  ],
  activeLayerId: 'layer-1',
  
  // 히스토리
  history: [],
  historyStep: -1,
  
  // 캔버스 상태
  isDrawing: false,
  canvasSize: { width: 800, height: 600 },
  // 현재 모드: 'draw' 또는 'upload'
  currentMode: 'draw'
}

// 리듀서 함수
function drawingReducer(state, action) {
  switch (action.type) {
    case 'SET_TOOL':
      return { ...state, tool: action.payload }
    
    case 'SET_COLOR':
      return { ...state, color: action.payload }
    
    case 'SET_BRUSH_SIZE':
      return { ...state, brushSize: action.payload }
    
    case 'SET_OPACITY':
      return { ...state, opacity: action.payload }
    
    case 'ADD_LAYER':
      const newLayer = {
        id: `layer-${Date.now()}`,
        name: `레이어 ${state.layers.length + 1}`,
        visible: true,
        active: false
      }
      return {
        ...state,
        layers: [...state.layers, newLayer]
      }
    
    case 'SET_ACTIVE_LAYER':
      return {
        ...state,
        layers: state.layers.map(layer => ({
          ...layer,
          active: layer.id === action.payload
        })),
        activeLayerId: action.payload
      }
    
    case 'TOGGLE_LAYER_VISIBILITY':
      return {
        ...state,
        layers: state.layers.map(layer =>
          layer.id === action.payload
            ? { ...layer, visible: !layer.visible }
            : layer
        )
      }
    
    case 'DELETE_LAYER':
      const filteredLayers = state.layers.filter(layer => layer.id !== action.payload)
      const deletedWasActive = state.activeLayerId === action.payload
      const newActiveLayerId = deletedWasActive && filteredLayers.length > 0
        ? filteredLayers[0].id
        : state.activeLayerId
      
      return {
        ...state,
        layers: filteredLayers.map((layer, index) => ({
          ...layer,
          active: layer.id === newActiveLayerId
        })),
        activeLayerId: newActiveLayerId
      }
    
    case 'SET_DRAWING_STATE':
      return { ...state, isDrawing: action.payload }
    
    case 'ADD_TO_HISTORY':
      const newHistory = state.history.slice(0, state.historyStep + 1)
      return {
        ...state,
        history: [...newHistory, action.payload],
        historyStep: state.historyStep + 1
      }
    
    case 'UNDO':
      return state.historyStep >= 0 
        ? { ...state, historyStep: Math.max(-1, state.historyStep - 1) }
        : state
    
    case 'REDO':
      return state.historyStep < state.history.length - 1
        ? { ...state, historyStep: state.historyStep + 1 }
        : state
    
    case 'SET_CURRENT_MODE':
      return { ...state, currentMode: action.payload }
    
    default:
      return state
  }
}

// Provider 컴포넌트
export function DrawingProvider({ children }) {
  const [state, dispatch] = useReducer(drawingReducer, initialState)
  
  return (
    <DrawingContext.Provider value={{ state, dispatch }}>
      {children}
    </DrawingContext.Provider>
  )
}

// 커스텀 훅
export function useDrawing() {
  const context = useContext(DrawingContext)
  if (!context) {
    throw new Error('useDrawing must be used within a DrawingProvider')
  }
  return context
}